import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { accountHasComplianceRequired } from '../../../../models/UiUtils';
import { KeyDateKind, ResourceSelectorId } from '../../../../viewmodels/AppViewModels';
import { AIScheduleSuccess, IAIScheduleSuccessProps } from '../../../containers/shared/AIScheduleSuccess';
import { CampaignSessionManager } from '../../../containers/shared/AIScheduleSuccess/campaignSessionManager';
import { baseStyleSheet } from '../../../styles/styles';
import { DidYouKnowSection } from '../../reporting/emails/DidYouKnowSection';
import { BouncedEmailsGraphic } from '../../svgs/graphics/BouncedEmailsGraphic';
import { UnsubscribeEmailGraphic } from '../../svgs/graphics/UnsubscribeEmailGraphic';
import { SendSchedulerComplete } from '../SendSchedulerComplete';
import { styleSheet } from './styles';

interface ISendSchedulerCompleteOverlayProps
	extends Omit<IAIScheduleSuccessProps, 'resourceType'>,
		IUserSessionComponentProps,
		IModalContext,
		Partial<RouteComponentProps<any>> {
	onCloseButtonClicked?(e: React.MouseEvent<HTMLElement>): void;
	styles?: StyleDeclarationValue[];
}

class _SendSchedulerCompleteOverlay extends React.Component<ISendSchedulerCompleteOverlayProps> {
	componentDidMount(): void {
		this.syncCampaignSession();
	}

	componentDidUpdate(prevProps: ISendSchedulerCompleteOverlayProps): void {
		if (
			prevProps.emailComposer !== this.props.emailComposer ||
			prevProps.emailComposerWithGuide !== this.props.emailComposerWithGuide ||
			prevProps.estimate !== this.props.estimate
		) {
			this.syncCampaignSession();
		}
	}

	private syncCampaignSession(): void {
		const hasActiveSession = CampaignSessionManager.hasActiveSession();

		if (!hasActiveSession) {
			return;
		}

		const composer = this.props.emailComposer ?? this.props.emailComposerWithGuide;
		if (!composer) {
			return;
		}

		const emailContext = CampaignSessionManager.createAIContextFromEmailData(composer);
		const scheduledSend = composer.emailMessage?.options?.scheduledSend;
		const scheduledDate = scheduledSend?.startDate ? new Date(scheduledSend.startDate) : undefined;
		const progress = emailContext
			? CampaignSessionManager.updateSessionProgress('email', emailContext, scheduledDate)
			: null;

		if (progress?.isCampaignComplete) {
			CampaignSessionManager.clearSession();

			if (this.props.parentModal?.onRequestClose) {
				this.props.parentModal.onRequestClose(composer.emailMessage, false);
			} else if (this.props.onCloseButtonClicked) {
				this.props.onCloseButtonClicked({} as React.MouseEvent<HTMLElement>);
			}
		}
	}

	private handleClose = () => {
		const { onCloseButtonClicked, emailComposerWithGuide } = this.props;

		const isSendNow = emailComposerWithGuide?.emailMessage?.options?.scheduledSend?.criteria === 'Immediately';

		if (CampaignSessionManager.isPrimaryResourceSendNow('email', isSendNow)) {
			CampaignSessionManager.clearSession();
			this.props.parentModal?.onRequestClose?.(emailComposerWithGuide?.emailMessage, false);
		} else if (onCloseButtonClicked) {
			onCloseButtonClicked({} as React.MouseEvent<HTMLElement>);
		}
	};

	private shouldShowAIScheduleSuccess = (emailComposer: any): boolean => {
		// Don't show AIScheduleSuccess for followup, birthday, or automation emails
		if (emailComposer?.isFollowUp) {
			return false;
		}

		if (
			emailComposer?.keyFact?.keyDate?.kind === KeyDateKind.Birthday ||
			emailComposer?.resourceSelector === ResourceSelectorId.HappyBirthday
		) {
			return false;
		}

		if (emailComposer?.resourceSelector && emailComposer.resourceSelector !== ResourceSelectorId.Undefined) {
			return false;
		}

		if (emailComposer?.automation || emailComposer?.automationId) {
			return false;
		}

		return true;
	};

	public render() {
		const { styles, onCloseButtonClicked, userSession, parentModal, ...restProps } = this.props;
		const emailComposer = restProps.emailComposer ?? restProps.emailComposerWithGuide;
		// Determine if this should show AIScheduleSuccess or traditional SendSchedulerComplete
		const shouldShowUnifiedSuccess = this.shouldShowAIScheduleSuccess(emailComposer);
		const isContentGenerationEnabled = userSession?.account?.features?.contentGeneration?.enabled;
		const requiresCompliance = accountHasComplianceRequired(userSession);

		if (shouldShowUnifiedSuccess && isContentGenerationEnabled) {
			return (
				<div className={css(styleSheet.overlay, ...(styles || []))}>
					<AIScheduleSuccess
						{...restProps}
						resourceType='email'
						className={css(styleSheet.overlayCompleteMessage)}
						hideHeader={true}
						requiresCompliance={requiresCompliance || emailComposer?.emailMessage?.options?.sendWithCompliance}
					/>
				</div>
			);
		}

		return (
			<div className={css(styleSheet.overlay, ...(styles || []))}>
				<SendSchedulerComplete {...restProps} className={css(styleSheet.overlayCompleteMessage)} />
				<button className={css(baseStyleSheet.ctaButton, styleSheet.overlayCloseButton)} onClick={onCloseButtonClicked}>
					Close Window
				</button>
				<div
					className={css(
						styleSheet.didYaKnowContainer,
						parentModal?.name === 'fullscreenModal' ? styleSheet.fullscreenDidYaKnowContainer : null
					)}
				>
					<DidYouKnowSection
						content={this.renderBounceContent()}
						graphic={<BouncedEmailsGraphic />}
						title='Did you know?'
					/>
					<DidYouKnowSection
						content={this.renderUnsubscribeContent()}
						graphic={<UnsubscribeEmailGraphic />}
						title='Did you know?'
					/>
				</div>
			</div>
		);
	}

	private renderBounceContent() {
		const provider = this.props.userSession.account.emailProviderConfiguration[0].emailProvider;
		if (provider === 'Gmail' || provider === 'Google' || provider === 'Office365') {
			return (
				<div>
					Some bounced emails are expected. Levitate will automatically tag the bad email addresses for you with
					&quot;Email Bounced&quot;.
				</div>
			);
		}
		return (
			<div>
				Some bounced emails are expected. Please
				<span className={css(styleSheet.bold)}> leave those bounced emails in your inbox for 12 hours, </span>
				and Levitate will automatically tag the bad email addresses for you with &quot;Email Bounced&quot;.
			</div>
		);
	}

	private renderUnsubscribeContent() {
		return (
			<div>
				If someone wishes to stop receiving future emails, look for the
				<span className={css(styleSheet.bold)}> “Unsubscribe this contact from my group emails” </span>
				option under their profile.
			</div>
		);
	}
}

const SendSchedulerCompleteOverlayAsObserver = observer(_SendSchedulerCompleteOverlay);
export const SendSchedulerCompleteOverlay = inject(
	UserSessionViewModelKey,
	ModalChildComponentContextKey
)(SendSchedulerCompleteOverlayAsObserver);
